import * as React from "react";
import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subDays,
} from "date-fns";
import { CalendarIcon, ChevronDown, X } from "lucide-react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Calendar,
} from "@repo/ui";
import { cn } from "@repo/utils";

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

export interface DateRangeFilterProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}

interface Preset {
  label: string;
  range: () => DateRange;
}

const PRESETS: Preset[] = [
  {
    label: "Today",
    range: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }),
  },
  {
    label: "This Week",
    range: () => ({
      from: startOfWeek(new Date(), { weekStartsOn: 0 }),
      to: endOfWeek(new Date(), { weekStartsOn: 0 }),
    }),
  },
  {
    label: "This Month",
    range: () => ({
      from: startOfMonth(new Date()),
      to: endOfMonth(new Date()),
    }),
  },
  {
    label: "This Year",
    range: () => ({
      from: startOfYear(new Date()),
      to: endOfYear(new Date()),
    }),
  },
  {
    label: "Last 30 Days",
    range: () => ({
      from: startOfDay(subDays(new Date(), 29)),
      to: endOfDay(new Date()),
    }),
  },
  {
    label: "Last 90 Days",
    range: () => ({
      from: startOfDay(subDays(new Date(), 89)),
      to: endOfDay(new Date()),
    }),
  },
];

function formatRange(from: Date | undefined, to: Date | undefined): string {
  if (!from && !to) return "Date range";
  if (from && !to) return format(from, "MMM d, yy") + " –";
  if (!from && to) return "– " + format(to, "MMM d, yy");
  return `${format(from!, "MMM d, yy")} – ${format(to!, "MMM d, yy")}`;
}

function getActivePresetLabel(
  from: Date | undefined,
  to: Date | undefined
): string | null {
  if (!from || !to) return null;
  for (const preset of PRESETS) {
    const r = preset.range();
    if (
      r.from &&
      r.to &&
      format(r.from, "yyyy-MM-dd") === format(from, "yyyy-MM-dd") &&
      format(r.to, "yyyy-MM-dd") === format(to, "yyyy-MM-dd")
    ) {
      return preset.label;
    }
  }
  return null;
}

export function DateRangeFilter({
  value,
  onChange,
  className,
}: DateRangeFilterProps) {
  const [open, setOpen] = React.useState(false);
  const [picking, setPicking] = React.useState<"from" | "to">("from");
  const [localFrom, setLocalFrom] = React.useState<Date | undefined>(
    value.from
  );
  const [localTo, setLocalTo] = React.useState<Date | undefined>(value.to);

  // Sync when parent resets
  React.useEffect(() => {
    setLocalFrom(value.from);
    setLocalTo(value.to);
  }, [value.from, value.to]);

  const hasValue = !!(value.from ?? value.to);
  const activePreset = getActivePresetLabel(value.from, value.to);
  const label = activePreset ?? formatRange(value.from, value.to);

  const applyPreset = (preset: Preset) => {
    const range = preset.range();
    setLocalFrom(range.from);
    setLocalTo(range.to);
    setPicking("from");
    onChange(range);
    setOpen(false);
  };

  const handleCustomClick = () => {
    // Reset local state so user can pick fresh custom dates
    setLocalFrom(undefined);
    setLocalTo(undefined);
    setPicking("from");
  };

  const handleDayClick = (day: Date) => {
    if (picking === "from") {
      setLocalFrom(day);
      setLocalTo(undefined);
      setPicking("to");
    } else {
      if (localFrom && day < localFrom) {
        const range = { from: day, to: localFrom };
        setLocalFrom(day);
        setLocalTo(localFrom);
        onChange(range);
      } else {
        setLocalTo(day);
        onChange({ from: localFrom, to: day });
      }
      setPicking("from");
      setOpen(false);
    }
  };

  const handleClear = () => {
    setLocalFrom(undefined);
    setLocalTo(undefined);
    setPicking("from");
    onChange({ from: undefined, to: undefined });
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      // Restore local state from committed value when closing without finishing
      setLocalFrom(value.from);
      setLocalTo(value.to);
      setPicking("from");
    }
  };

  const isCustomActive = !activePreset && hasValue;

  return (
    // Wrapper lets us position the X button outside the PopoverTrigger
    <div className={cn("relative inline-flex items-center", className)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-9 gap-1.5 font-normal",
              hasValue ? "border-primary/50 text-primary pr-7" : "pr-2"
            )}
          >
            <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="max-w-[200px] truncate">{label}</span>
            {!hasValue && (
              <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex">
            {/* Presets sidebar */}
            <div className="flex flex-col border-r border-border p-2 gap-0.5 min-w-[130px]">
              {PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-left text-sm transition-colors hover:bg-primary-soft hover:text-primary",
                    activePreset === preset.label &&
                      "bg-primary-soft text-primary font-medium"
                  )}
                >
                  {preset.label}
                </button>
              ))}
              <div className="my-1 border-t border-border" />
              <button
                type="button"
                onClick={handleCustomClick}
                className={cn(
                  "rounded-md px-3 py-1.5 text-left text-sm transition-colors hover:bg-primary-soft hover:text-primary",
                  isCustomActive && "bg-primary-soft text-primary font-medium"
                )}
              >
                Custom
              </button>
            </div>

            {/* Calendar */}
            <div className="p-2">
              <p className="mb-1 px-1 text-xs text-muted-foreground">
                {picking === "from" ? "Select start date" : "Select end date"}
              </p>
              <Calendar
                mode="single"
                selected={picking === "from" ? localFrom : localTo}
                onDayClick={(day) => handleDayClick(day)}
                modifiers={{
                  range_start: localFrom ? [localFrom] : [],
                  range_end: localTo ? [localTo] : [],
                  range_middle:
                    localFrom && localTo
                      ? { from: localFrom, to: localTo }
                      : undefined,
                }}
                modifiersClassNames={{
                  range_start:
                    "bg-primary text-primary-foreground rounded-l-md",
                  range_end: "bg-primary text-primary-foreground rounded-r-md",
                  range_middle: "bg-primary-soft text-primary rounded-none",
                }}
              />
              {localFrom && (
                <p className="mt-1 px-1 text-xs text-muted-foreground text-center">
                  {localTo
                    ? `${format(localFrom, "MMM d")} – ${format(localTo, "MMM d, yyyy")}`
                    : `${format(localFrom, "MMM d, yyyy")} – pick end date`}
                </p>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* X is outside the PopoverTrigger so it doesn't toggle the popover */}
      {hasValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2 flex items-center justify-center text-primary/60 hover:text-primary transition-colors"
          aria-label="Clear date range"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
